import { useMemo, useState } from "react";
import { IChat, IConversation, IUserfeedback, ISupportTicketResponse, IPrivacyCheck } from "system/types/interfaces/IAskAiden";
import HttpMethods from "system/types/enums/HttpMethods";
import useHttpRequest from "./use-http-request";
import authentication from "system/libraries/authentication";

interface IUrlManagementItem {
  id: number;
  url: string;
  description: string;
  dateCreated: string;
  dateModified: string;
  modifiedBy: string;
  createdBy: string;
}

interface IUrlManagementRequest {
  UrlValue: string;
  Description: string;
}

interface IUpdateUrlRequest {
  UrlValue: string;
  Description: string;
}

interface IDocumentManagementItem {
  id: number;
  name: string;
  description: string;
  type: string;
  dateCreated: string;
  dateModified: string;
  modifiedBy: string;
  createdBy: string;
}

interface IDocumentFilterItem {
  field: string;
  operator: string;
  value?: unknown;
}

interface IDocumentManagementQuery {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  filters?: IDocumentFilterItem[];
}

interface IDocumentManagementPage {
  items: IDocumentManagementItem[];
  totalCount: number;
}

interface IUrlManagementQuery {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  filters?: IDocumentFilterItem[];
}

interface IUrlManagementPage {
  items: IUrlManagementItem[];
  totalCount: number;
}

// The Azure Functions host owns /admin/* for its own management API and answers those
// requests itself, so the function serves these routes under /api/v1.0 instead. Keep in
// sync with ask-aiden's api_app routes.
const ADMIN_ROUTE_PREFIX = "/api/v1.0";

const buildAdminUrl = (path: string, code: string, extraParams?: Record<string, string>) => {
  const baseUrl = (process.env.REACT_APP_ASKAIDEN_FUNCTION_URL ?? "").replace(/\/$/, "");
  const query = new URLSearchParams({ code, ...extraParams });
  return `${baseUrl}${ADMIN_ROUTE_PREFIX}${path}?${query.toString()}`;
};

interface IPydanticErrorDetail {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

export const isAskAidenApiError = (response: any): boolean => {
  // A null/undefined response means useHttpRequest short-circuited (no token, session
  // logged out) rather than the call actually succeeding - treat it as a failure rather
  // than silently reporting success, same as an explicit failMessage/detail below.
  if (!response) return true;
  return !!(response.failMessage || response.detail !== undefined);
};

export const getAskAidenErrorMessage = (response: any, fallback: string): string => {
  if (!response) return fallback;
  if (typeof response.failMessage === "string" && response.failMessage.trim()) {
    return response.failMessage;
  }
  const { detail } = response;
  if (Array.isArray(detail)) {
    const messages = (detail as IPydanticErrorDetail[]).map((entry) => entry?.msg).filter((msg): msg is string => !!msg);
    if (messages.length) return messages.join("; ");
  } else if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  return fallback;
};

const buildAdminDocumentUrl = (path: string, container: string, code: string, extraParams?: Record<string, string>) => {
  const baseUrl = (process.env.REACT_APP_ASKAIDEN_FUNCTION_URL ?? "").replace(/\/$/, "");
  const query = new URLSearchParams({ container, code, ...extraParams });
  return `${baseUrl}${ADMIN_ROUTE_PREFIX}${path}?${query.toString()}`;
};

const useAskAidenApi = () => {
  const [auth] = useState(authentication);

  const askAidenApiUrl = useMemo<string>(() => (process.env.REACT_APP_API_ASKAIDEN ?? "") + "/api/v1.0/", []);
  const askAidenAdminApiUrl = useMemo<string>(() => (process.env.REACT_APP_ASKAIDEN_FUNCTION_URL ?? ""), []);
  const askAidenFunctionCode = useMemo<string>(() => process.env.REACT_APP_ASKAIDEN_FUNCTION_CODE ?? "", []);
  const askAidenContainerName = useMemo<string>(() => process.env.REACT_APP_ASKAIDEN_CONTAINER_NAME ?? "", []);

  const httpRequest = useHttpRequest(askAidenApiUrl, auth);
  const askAidenAdminHttpRequest = useHttpRequest(askAidenAdminApiUrl, auth);

  return useMemo(
    () => ({
      chat: {
        prompts: async (): Promise<IChat> => {
          return await httpRequest(HttpMethods.get, "chat/staticPrompts");
        },
        conversation: async (requestData: object): Promise<IConversation> => {
          return await httpRequest(HttpMethods.post, "chat/userQuery", requestData);
        },
        feedback: async (userFeedback: object): Promise<IUserfeedback> => {
          return await httpRequest(HttpMethods.post, "chat/userSatisfaction", userFeedback);
        },
        supportTicket: async (ticketData: object): Promise<ISupportTicketResponse> => {
          return await httpRequest(HttpMethods.post, "chat/supportTicket", ticketData);
        },
        privacyCheck: async(userEmail: string):Promise<IPrivacyCheck> =>{
          return await httpRequest(HttpMethods.get,`privacy-policy?email=${userEmail}`,)
        },
        privacyAcceptance: async(userData : object):Promise<IPrivacyCheck> =>{
          return await httpRequest(HttpMethods.put,"privacy-policy",userData)
        }
      },
      documents: {
        urlManagement: async ({
          page,
          pageSize,
          search,
          sortBy,
          sortDir,
          filters,
        }: IUrlManagementQuery): Promise<IUrlManagementPage> => {
          const response = await askAidenAdminHttpRequest(
            HttpMethods.get,
            buildAdminUrl("/admin/urls", askAidenFunctionCode, {
              page: String(page),
              page_size: String(pageSize),
              ...(search ? { search } : {}),
              ...(sortBy ? { sort_by: sortBy } : {}),
              ...(sortDir ? { sort_dir: sortDir } : {}),
              ...(filters && filters.length ? { filters: JSON.stringify(filters) } : {}),
            })
          );
          if (isAskAidenApiError(response)) {
            throw new Error(getAskAidenErrorMessage(response, "Failed to load URLs"));
          }
          return {
            items: response?.data || [],
            totalCount: response?.totalCount ?? 0,
          };
        },
        documentManagement: async ({
          page,
          pageSize,
          search,
          sortBy,
          sortDir,
          filters,
        }: IDocumentManagementQuery): Promise<IDocumentManagementPage> => {
          const response = await askAidenAdminHttpRequest(
            HttpMethods.get,
            buildAdminDocumentUrl("/admin/documents", askAidenContainerName, askAidenFunctionCode, {
              page: String(page),
              page_size: String(pageSize),
              ...(search ? { search } : {}),
              ...(sortBy ? { sort_by: sortBy } : {}),
              ...(sortDir ? { sort_dir: sortDir } : {}),
              ...(filters && filters.length ? { filters: JSON.stringify(filters) } : {}),
            })
          );
          if (isAskAidenApiError(response)) {
            throw new Error(getAskAidenErrorMessage(response, "Failed to load documents"));
          }
          return {
            items: response?.value || [],
            totalCount: response?.totalCount ?? 0,
          };
        },
        // CreatedBy/ModifiedBy are deliberately not sent - the server takes them from the
        // validated admin's token (see admin_auth.py / api_app/__init__.py), never from the
        // request body, so a client-supplied name here would just be ignored.
        createUrl: async (payload: Partial<IUrlManagementItem>) => {
          const requestPayload: IUrlManagementRequest = {
            UrlValue: payload.url ?? "",
            Description: payload.description ?? "",
          };

          return await askAidenAdminHttpRequest(HttpMethods.post, buildAdminUrl("/admin/url", askAidenFunctionCode), requestPayload);
        },
        updateUrl: async (id: number, payload: Partial<IUrlManagementItem>) => {
          const requestPayload: IUpdateUrlRequest = {
            UrlValue: payload.url ?? "",
            Description: payload.description ?? "",
          };

          return await askAidenAdminHttpRequest(HttpMethods.put, buildAdminUrl(`/admin/url/${id}`, askAidenFunctionCode), requestPayload);
        },
        deleteUrl: async (id: number) => {
          return await askAidenAdminHttpRequest(HttpMethods.delete, buildAdminUrl(`/admin/url/${id}`, askAidenFunctionCode));
        },
        createDocument: async (formData: FormData) => {
          return await askAidenAdminHttpRequest(HttpMethods.post, buildAdminUrl("/admin/upload-document", askAidenFunctionCode), formData);
        },
        updateDocument: async (id: number, newFileName: string, description: string) => {
          return await askAidenAdminHttpRequest(
            HttpMethods.put,
            buildAdminUrl(`/admin/document/${id}`, askAidenFunctionCode, { new_file_name: newFileName, description })
          );
        },
        deleteDocument: async (id: number) => {
          return await askAidenAdminHttpRequest(HttpMethods.delete, buildAdminUrl(`/admin/document/${id}`, askAidenFunctionCode));
        },
      },
    }),
    [askAidenAdminHttpRequest, askAidenFunctionCode, httpRequest, askAidenContainerName]
  );
};

export default useAskAidenApi;
